import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, AccountInfo } from '@azure/msal-browser';
import { Store } from '@ngxs/store';
import { environment } from '../../../environments/environment';
import { IdTokenClaims } from '../../shared/interfaces/IdTokenClaims';
import { ClearUser, LoadUser } from '../state/user.actions';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly store = inject(Store);

    private readonly interactionStatus = toSignal(this.msalBroadcastService.inProgress$);
    private readonly msalEvent = toSignal(this.msalBroadcastService.msalSubject$);

    private readonly _userAccount = signal<AccountInfo | null>(null);
    private _previousPhotoUrl: string | null = null;
    private readonly _userPhotoResource = httpResource.blob(
        () => {
            const oid = this.userIdTokenClaims()?.oid;
            return oid ? `${environment.apiConfig[0].uri}/users/${oid}/photo/$value` : undefined;
        },
        {
            parse: blob => {
                if (this._previousPhotoUrl) {
                    URL.revokeObjectURL(this._previousPhotoUrl);
                }
                const url = URL.createObjectURL(blob);
                this._previousPhotoUrl = url;
                return url;
            }
        }
    );

    public readonly isAuthenticated = computed(() => this._userAccount() !== null);
    public readonly isAuthLoading = computed(() => this.interactionStatus() !== InteractionStatus.None);
    public readonly userIdTokenClaims = computed(() => (this._userAccount()?.idTokenClaims as IdTokenClaims) ?? null);
    public readonly userPhoto = computed(() => this._userPhotoResource.value());
    public readonly userName = computed(() => {
        const claims = this.userIdTokenClaims();
        return claims ? `${claims.preferred_family_name}, ${claims.preferred_given_name}` : '';
    });

    constructor() {
        effect(() => this.onInteractionStatusChange());
        effect(() => this.onMsalEvent());
    }

    public logout(): void {
        this.store.dispatch(new ClearUser());
        this.msalService.logoutRedirect();
    }

    private onInteractionStatusChange(): void {
        if (this.interactionStatus() === InteractionStatus.None) {
            this.resolveActiveAccount();
        }
    }

    private onMsalEvent(): void {
        if (this.msalEvent()?.eventType === EventType.LOGOUT_SUCCESS) {
            this._userAccount.set(null);
        }
    }

    private resolveActiveAccount(): void {
        const accounts = this.msalService.instance.getAllAccounts();
        const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0] ?? null;

        this._userAccount.set(activeAccount);

        if (activeAccount) {
            this.msalService.instance.setActiveAccount(activeAccount);
            this.store.dispatch(new LoadUser());
        }
    }
}
